import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {ColumnDef} from '@tanstack/react-table';
import {usePermissions} from 'tmwx-design-common';
import {AppDispatch, RootState} from '../../gui-common/redux/store';
import {MqttGrab, MqttMessageDeleteTarget} from '../../gui-common/redux/data/mqttGrab';
import {deleteMqttMessages, getMqttGrabList} from '../../gui-common/redux/action/efe/mqttGrabAction';
import {mqttGrabActions} from '../../gui-common/redux/slice/efe/mqttGrabSlice';
import {setSnackbarData} from '../../gui-common/redux/action/snackbarAction';
import {Alert, Button, Grid, Header, Modal, Popup, styles, Surface, Table, Text, useTheme} from '../../tmwx-design-web/src';
import MqttMessageSaveSwitch from '../../components/efe/MqttMessageSaveSwitch';

const MqttMessages = () => {
    const {t} = useTranslation();
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const {hasRights} = usePermissions();
    const {padding} = useTheme();
    const canRead = hasRights(['efe_mqtt_grab_list']);
    const canDelete = hasRights(['efe_mqtt_message_delete']);
    const canEdit = hasRights(['efe_mqtt_grab_edit']);
    const systemId = searchParams.get('systemId') || '';
    const podId = searchParams.get('podId') || '';
    const {messages, loading, error} = useSelector((state: RootState) => state.mqttGrab);
    const [selectedMessage, setSelectedMessage] = useState<MqttGrab | null>(null);
    const [selectedContent, setSelectedContent] = useState<'payload' | 'processingLog'>('payload');
    const [deleting, setDeleting] = useState(false);
    const pendingRequest = useRef<ReturnType<ReturnType<typeof getMqttGrabList>> | null>(null);
    const pendingDeletion = useRef<ReturnType<ReturnType<typeof deleteMqttMessages>> | null>(null);

    const refresh = useCallback(() => {
        if (!canRead || !systemId.trim()) return;
        pendingRequest.current?.abort();
        pendingRequest.current = dispatch(getMqttGrabList(systemId));
    }, [canRead, systemId, dispatch]);

    useEffect(() => {
        setSelectedMessage(null);
        setDeleting(false);
        dispatch(mqttGrabActions.clear());
        refresh();
        return () => {
            pendingRequest.current?.abort();
            pendingDeletion.current?.abort();
            pendingDeletion.current = null;
            dispatch(mqttGrabActions.clear());
        };
    }, [dispatch, refresh]);

    const handleDelete = useCallback(async (target: MqttMessageDeleteTarget) => {
        if (!canRead || !canDelete || !systemId.trim() || pendingDeletion.current) return;
        const request = dispatch(deleteMqttMessages({...target, systemId}));
        pendingDeletion.current = request;
        setDeleting(true);
        try {
            await request.unwrap();
            if (pendingDeletion.current !== request) return;
            dispatch(setSnackbarData({status: 'success', text: t('actionSuccess')}));
            refresh();
        } catch {
            if (pendingDeletion.current === request) {
                dispatch(setSnackbarData({status: 'danger', text: t('errorOccurred')}));
            }
        } finally {
            if (pendingDeletion.current === request) {
                pendingDeletion.current = null;
                setDeleting(false);
            }
        }
    }, [canRead, canDelete, systemId, dispatch, t, refresh]);

    const copyPayload = useCallback(async (payload: string) => {
        try {
            await navigator.clipboard.writeText(payload);
            dispatch(setSnackbarData({status: 'success', text: t('copyToClipboard')}));
        } catch {
            dispatch(setSnackbarData({status: 'danger', text: t('errorOccurred')}));
        }
    }, [dispatch, t]);

    const columns = useMemo<ColumnDef<MqttGrab>[]>(() => [
        {
            accessorKey: 'receivedAt',
            meta: t('efe.mqttReceivedAt'),
            size: 190,
            header: () => <Text bold>{t('efe.mqttReceivedAt')}</Text>,
            cell: ({row}) => <Text style={{whiteSpace: 'nowrap'}}>{row.original.receivedAt}</Text>
        },
        {
            accessorKey: 'messageType',
            meta: t('efe.mqttMessageType'),
            size: 110,
            header: () => <Text bold>{t('efe.mqttMessageType')}</Text>
        },
        {
            id: 'subtype',
            accessorFn: message => JSON.stringify(message.subtype),
            meta: t('efe.mqttSubtype'),
            size: 180,
            header: () => <Text bold>{t('efe.mqttSubtype')}</Text>
        },
        {
            accessorKey: 'payload',
            meta: t('efe.mqttMessage'),
            size: 600,
            header: () => <Text bold>{t('efe.mqttMessage')}</Text>,
            cell: ({row, column}) => <Text style={{whiteSpace: 'nowrap', maxWidth: column.getSize(), overflow: 'hidden', textOverflow: 'ellipsis'}}>
                {row.original.payload.slice(0, 200)}{row.original.payload.length > 200 ? '…' : ''}
            </Text>
        },
        {
            id: 'actions',
            meta: t('actions'),
            size: 140,
            enableSorting: false,
            header: () => <Text bold centered>{t('actions')}</Text>,
            cell: ({row}) => <div style={styles.row}>
                <Popup type='hover' style={styles.marginRight4} triggerComponent={
                    <Button iconLeft='jump_to_element_outlined' status='primary' size='small' onPress={() => {
                        setSelectedContent('payload');
                        setSelectedMessage(row.original);
                    }} />
                }>
                    <Surface style={{padding}} elevation={1}><Text>{t('details')}</Text></Surface>
                </Popup>
                <Popup type='hover' style={styles.marginRight4} triggerComponent={
                    <Button iconLeft='content_copy' status='info' size='small' onPress={() => { void copyPayload(row.original.payload); }} />
                }>
                    <Surface style={{padding}} elevation={1}><Text>{t('copy')}</Text></Surface>
                </Popup>
                {canDelete && <Popup type='hover' style={styles.marginRight4} triggerComponent={
                    <Button iconLeft='delete' size='small' disabled={deleting || loading}
                        onPress={() => { void handleDelete({scope: 'message', mqttMessageId: row.original.id}); }} />
                }>
                    <Surface style={{padding}} elevation={1}><Text>{t('delete')}</Text></Surface>
                </Popup>}
                {row.original.processingLog !== null && row.original.processingLog !== undefined &&
                    <Popup type='hover' style={styles.marginRight4} triggerComponent={
                        <Button iconLeft='bug_report' size='small' onPress={() => {
                            setSelectedContent('processingLog');
                            setSelectedMessage(row.original);
                        }} />
                    }>
                        <Surface style={{padding}} elevation={1}><Text>{t('efe.mqttProcessingLog')}</Text></Surface>
                    </Popup>
                }
            </div>
        }
    ], [t, padding, canDelete, copyPayload, deleting, loading, handleDelete]);

    return (
        <Surface style={{height: '100vh'}} elevation={3}>
            <Header title={t('efe.mqttMessages')} subtitle={[podId, systemId].filter(Boolean).join(' - ')} rounded
                actionLeft={<Button iconLeft='arrow_back' status='neutral' variant='ghost' onPress={() => navigate(-1)} />} />
            <Grid.Container maxWidth='xl' centered>
                <Grid.Col m={12} lg={12} xl={12}>
                    {error && <Alert status='danger' text={t('errorOccurred')} />}
                    <Surface rounded>
                        {canRead && <Table
                            columns={columns}
                            data={messages}
                            loading={loading}
                            refreshData={refresh}
                            dense
                            useGlobalFilter
                            toolbarActions={<>
                                {canEdit && systemId.trim() && <MqttMessageSaveSwitch key={systemId} systemId={systemId} />}
                                {canDelete && <Popup type='hover' triggerComponent={
                                <Button iconLeft='delete' status='neutral' variant='ghost' disabled={deleting || loading || !systemId.trim()}
                                    onPress={() => { void handleDelete({scope: 'all'}); }} />
                            }>
                                <Surface style={{padding}} elevation={2}><Text>{t('efe.mqttDeleteAll')}</Text></Surface>
                                </Popup>}
                            </>}
                            labels={{
                                add: t('add'), deletePreferences: t('deletePreferences'), jumpHere: t('jumpHere'),
                                page: t('page'), refresh: t('refresh'), row: t('row'), rowsSelected: t('rowsSelected'),
                                savePreferencies: t('savePreferences'), searchData: t('searchData'),
                                selectCurrentPageData: t('selectCurrentPageData'), showAll: t('showAll'), sizing: t('sizing')
                            }}
                        />}
                    </Surface>
                </Grid.Col>
            </Grid.Container>
            <Modal visible={selectedMessage !== null && canRead} type='dialog' dialogMaxWidth='m' contentElevation={0}
                closeModal={() => setSelectedMessage(null)}
                header={<Header title={t(selectedContent === 'payload' ? 'efe.mqttPayload' : 'efe.mqttProcessingLog')} actionRight={
                    <Button iconLeft='clear' status='neutral' variant='ghost' onPress={() => setSelectedMessage(null)} />
                } />}>
                <Surface style={{padding}}>
                    <Text style={{whiteSpace: 'pre-wrap', fontFamily: 'monospace', overflowWrap: 'anywhere'}}>
                        {selectedContent === 'payload' ? selectedMessage?.payload : selectedMessage?.processingLog}
                    </Text>
                </Surface>
            </Modal>
        </Surface>
    );
};

export default MqttMessages;
